import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';

interface FeedbackButtonsProps {
  messageId: string;
  sessionId: string;
  userMessage: string;
  assistantMessage: string;
  /** Demo Mode: show the full feedback flow but never send anything to the API. */
  demo?: boolean;
}

export default function FeedbackButtons({ messageId, sessionId, userMessage, assistantMessage, demo = false }: FeedbackButtonsProps) {
  const { t } = useTranslation('feedback');
  const { idToken } = useAuth();
  const [submitted, setSubmitted] = useState<'positive' | 'negative' | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showComment, setShowComment] = useState(false);
  const [comment, setComment] = useState('');
  const [pendingRating, setPendingRating] = useState<'positive' | 'negative' | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Focus textarea when the modal opens
  useEffect(() => {
    if (showComment && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [showComment]);

  // Close modal on Escape (does not submit feedback)
  useEffect(() => {
    if (!showComment) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSubmitting) {
        setShowComment(false);
        setPendingRating(null);
        setComment('');
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showComment, isSubmitting]);

  const submitFeedback = async (rating: 'positive' | 'negative', feedbackComment: string = '') => {
    if (isSubmitting || submitted) return;

    setIsSubmitting(true);
    if (demo) {
      // Scripted demo: acknowledge locally so the presenter can show the flow
      // without writing rows into the real feedback dashboard.
      setSubmitted(rating);
      setShowComment(false);
      setIsSubmitting(false);
      return;
    }
    try {
      const apiUrl =
        window.__PAILA_CONFIG__?.apiUrl || import.meta.env.VITE_API_URL || '/api';
      await fetch(`${apiUrl}/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          session_id: sessionId,
          message_id: messageId,
          rating,
          user_message: userMessage,
          assistant_message: assistantMessage,
          ...(feedbackComment && { comment: feedbackComment }),
        }),
      });
      setSubmitted(rating);
      setShowComment(false);
    } catch (error) {
      console.error('Error submitting feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleThumbClick = (rating: 'positive' | 'negative') => {
    if (isSubmitting || submitted) return;
    setPendingRating(rating);
    setShowComment(true);
  };

  const handleSkip = () => {
    if (pendingRating) {
      submitFeedback(pendingRating);
    }
  };

  const handleSubmitComment = () => {
    if (pendingRating) {
      submitFeedback(pendingRating, comment.trim());
    }
  };

  const handleBackdropClick = () => {
    if (isSubmitting) return;
    setShowComment(false);
    setPendingRating(null);
    setComment('');
  };

  if (submitted) {
    return (
      <div className="flex items-center space-x-1 mt-1" role="status" aria-live="polite">
        <span className="text-xs text-gray-400 dark:text-gray-500">
          {submitted === 'positive' ? t('thanksPositive') : t('thanksNegative')}
        </span>
      </div>
    );
  }

  // Thumb buttons rendered both inline (under the message) and inside the
  // modal (so switching the rating after opening still works). Sharing the
  // markup via a small helper keeps aria-labels identical in both places.
  const renderThumbs = (size: 'inline' | 'modal') => {
    const sizing = size === 'modal' ? 'w-12 h-12 p-3' : 'p-2';
    const iconSize = size === 'modal' ? 'w-6 h-6' : 'w-4 h-4';
    return (
      <div className={`flex items-center ${size === 'modal' ? 'justify-center space-x-3' : 'space-x-2'}`}>
        <button
          onClick={() => handleThumbClick('positive')}
          disabled={isSubmitting}
          className={`${sizing} rounded-lg transition-colors disabled:opacity-50 ${
            pendingRating === 'positive'
              ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
              : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-green-600 dark:hover:text-green-400'
          }`}
          title={t('helpful')}
          aria-label={t('markHelpful')}
          aria-pressed={pendingRating === 'positive'}
        >
          <svg className={iconSize} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5"
            />
          </svg>
        </button>
        <button
          onClick={() => handleThumbClick('negative')}
          disabled={isSubmitting}
          className={`${sizing} rounded-lg transition-colors disabled:opacity-50 ${
            pendingRating === 'negative'
              ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
              : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-red-600 dark:hover:text-red-400'
          }`}
          title={t('notHelpful')}
          aria-label={t('markNotHelpful')}
          aria-pressed={pendingRating === 'negative'}
        >
          <svg className={iconSize} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06l3.76.94m-7 10v5a2 2 0 002 2h.096c.5 0 .905-.405.905-.904 0-.715.211-1.413.608-2.008L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5"
            />
          </svg>
        </button>
      </div>
    );
  };

  return (
    <>
      {/* Inline thumbs under the message — hidden when the modal is open
          so aria-label lookups (used by the test suite) resolve uniquely. */}
      {!showComment && <div className="mt-1">{renderThumbs('inline')}</div>}

      {showComment && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
          onClick={handleBackdropClick}
          role="dialog"
          aria-modal="true"
          aria-labelledby="feedback-modal-title"
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2
              id="feedback-modal-title"
              className="text-lg font-semibold text-center text-gray-900 dark:text-white mb-1"
            >
              {pendingRating === 'positive' ? t('helpful') : t('notHelpful')}
            </h2>
            <p className="text-sm text-center text-gray-600 dark:text-gray-400 mb-4">
              {t('commentPlaceholder')}
            </p>

            <div className="mb-5">{renderThumbs('modal')}</div>

            <div className="relative mb-5" role="region" aria-label={t('feedbackComment')}>
              <textarea
                ref={textareaRef}
                value={comment}
                onChange={(e) => setComment(e.target.value.slice(0, 500))}
                placeholder={t('commentPlaceholder')}
                aria-label={t('commentAriaLabel')}
                maxLength={500}
                rows={6}
                className="w-full px-4 py-3 text-base border border-gray-300 dark:border-gray-600
                  rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white
                  focus:ring-2 focus:ring-uel-purple-500 focus:border-transparent resize-none"
              />
              <span
                className="absolute bottom-2 right-3 text-xs text-gray-400 dark:text-gray-500"
                aria-live="polite"
              >
                {comment.length}/500
              </span>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleSkip}
                disabled={isSubmitting}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300
                  bg-gray-100 dark:bg-gray-700 rounded-lg
                  hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                aria-label={t('skipAriaLabel')}
              >
                {t('skip')}
              </button>
              <button
                onClick={handleSubmitComment}
                disabled={isSubmitting || !comment.trim()}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-uel-purple-600
                  hover:bg-uel-purple-700 rounded-lg transition-colors
                  disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label={t('submitAriaLabel')}
              >
                {isSubmitting ? t('submitting') : t('submit')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
